import type { FC } from 'react';

import type { AdminPaymentRecord } from '@/types/payment.types';
import { formatRupeesExact } from '@/utils/currency.util';

import type { PaymentActionForm } from '../hooks/usePaymentsPage.hook';
import { COLLECTION_STATUS_LABEL, SETTLEMENT_STATUS_LABEL } from '../constants';

interface PaymentDetailDrawerProps {
  record: AdminPaymentRecord;
  isMutating: boolean;
  actionError: string | null;
  openForm: PaymentActionForm;
  draft: string;
  onClose: () => void;
  onOpenForm: (form: PaymentActionForm) => void;
  onDraftChange: (value: string) => void;
  onVerify: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSettle: (id: string, adminRef: string) => void;
  onRefundDeposit: (id: string, ref?: string) => void;
}

const Line: FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[12px] text-[var(--color-ink-muted)]">{label}</span>
    <span className={`text-[12px] ${strong ? 'font-bold text-[var(--color-ink)]' : 'text-[var(--color-ink)]'}`}>
      {value}
    </span>
  </div>
);

const secondaryBtn =
  'rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-[12px] font-semibold text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] disabled:opacity-50';

const PaymentDetailDrawer: FC<PaymentDetailDrawerProps> = ({
  record,
  isMutating,
  actionError,
  openForm,
  draft,
  onClose,
  onOpenForm,
  onDraftChange,
  onVerify,
  onReject,
  onSettle,
  onRefundDeposit,
}) => {
  const a = record.amounts;
  const canVerify = record.collectionStatus === 'payment_submitted';
  // Settlement / deposit refund only after the rental is returned (readyToSettle).
  const canSettle =
    record.readyToSettle && record.settlementStatus === 'pending';
  const canRefund =
    record.readyToSettle && record.depositRefundStatus === 'pending' && a.depositPaise > 0;
  const awaitingReturn =
    record.collectionStatus === 'verified' && !record.readyToSettle && record.settlementStatus === 'pending';

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--color-divider)] p-4">
          <div>
            <p className="font-mono text-[14px] font-bold text-[var(--color-ink)]">{record.paymentCode}</p>
            <p className="text-[11px] text-[var(--color-ink-muted)]">Booking {record.bookingCode}</p>
          </div>
          <button type="button" onClick={onClose} className={secondaryBtn}>
            Close
          </button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${COLLECTION_STATUS_LABEL[record.collectionStatus].tone}`}
            >
              {COLLECTION_STATUS_LABEL[record.collectionStatus].label}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${SETTLEMENT_STATUS_LABEL[record.settlementStatus].tone}`}
            >
              {SETTLEMENT_STATUS_LABEL[record.settlementStatus].label}
            </span>
          </div>

          <p className="text-[12px] text-[var(--color-ink)]">
            {record.borrower.name} → {record.owner.name}
            {record.campusName ? ` · ${record.campusName}` : ''}
          </p>

          {/* Breakdown */}
          <div className="rounded-xl border border-[var(--color-divider)] p-3">
            <Line label="Rental" value={formatRupeesExact(a.rentalPaise)} />
            <Line label={`Commission (${a.commissionPct}%)`} value={formatRupeesExact(a.commissionPaise)} />
            <Line label={`GST on fee (${a.gstPct}%)`} value={formatRupeesExact(a.gstOnCommissionPaise)} />
            {a.depositPaise > 0 ? <Line label="Deposit" value={formatRupeesExact(a.depositPaise)} /> : null}
            <div className="my-1 border-t border-[var(--color-divider)]" />
            <Line label="Borrower paid" value={formatRupeesExact(a.borrowerTotalPaise)} strong />
            <div className="my-1 border-t border-[var(--color-divider)]" />
            <Line label={`Settlement fee (${a.settlementFeePct}%)`} value={`−${formatRupeesExact(a.settlementFeePaise)}`} />
            <Line label="GST on settlement fee" value={`−${formatRupeesExact(a.gstOnSettlementFeePaise)}`} />
            <Line label="Owner payout" value={formatRupeesExact(a.ownerPayoutPaise)} strong />
            <Line label="GST liability" value={formatRupeesExact(a.gstLiabilityPaise)} />
          </div>

          {/* Proof */}
          {record.borrowerRef || record.proofUrl ? (
            <div className="rounded-xl border border-[var(--color-divider)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Payment proof
              </p>
              {record.borrowerRef ? (
                <p className="mt-1 font-mono text-[13px] text-[var(--color-ink)]">{record.borrowerRef}</p>
              ) : null}
              {record.proofUrl ? (
                <a href={record.proofUrl} target="_blank" rel="noreferrer">
                  <img
                    src={record.proofUrl}
                    alt="Payment proof"
                    className="mt-2 max-h-64 w-full rounded-lg object-contain"
                  />
                </a>
              ) : null}
            </div>
          ) : null}

          {/* Timeline */}
          <div className="rounded-xl border border-[var(--color-divider)] p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
              Timeline
            </p>
            <ol className="flex flex-col gap-2">
              {record.timeline.map((t, i) => (
                <li key={`${t.event}-${i}`} className="text-[12px] text-[var(--color-ink)]">
                  <span className="font-semibold">{t.event.replace(/_/g, ' ')}</span>
                  <span className="text-[var(--color-ink-muted)]"> · {new Date(t.at).toLocaleString()}</span>
                  {t.note ? <span className="block text-[11px] text-[var(--color-ink-muted)]">{t.note}</span> : null}
                </li>
              ))}
            </ol>
          </div>

          {actionError ? <p className="text-[12px] text-red-600">{actionError}</p> : null}

          {awaitingReturn ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
              Payment verified — waiting for the borrower to return the item. You can settle once
              the rental is marked returned.
            </p>
          ) : null}

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {canVerify ? (
              <>
                <button
                  type="button"
                  disabled={isMutating}
                  onClick={() => onVerify(record.id)}
                  className="rounded-lg bg-emerald-600 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isMutating ? 'Working…' : 'Verify payment'}
                </button>
                {openForm === 'reject' ? (
                  <div className="rounded-lg border border-[var(--color-divider)] p-3">
                    <textarea
                      value={draft}
                      onChange={(e) => onDraftChange(e.target.value)}
                      placeholder="Reason (shown to the borrower)"
                      className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                      rows={2}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        disabled={isMutating || draft.trim().length < 3}
                        onClick={() => onReject(record.id, draft.trim())}
                        className="rounded-lg bg-red-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Confirm reject
                      </button>
                      <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => onOpenForm('reject')} className={secondaryBtn}>
                    Reject payment
                  </button>
                )}
              </>
            ) : null}

            {canSettle ? (
              openForm === 'settle' ? (
                <div className="rounded-lg border border-[var(--color-divider)] p-3">
                  <input
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    placeholder="Bank/UTR reference of the transfer"
                    className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={isMutating || draft.trim().length < 3}
                      onClick={() => onSettle(record.id, draft.trim())}
                      className="rounded-lg bg-brand-500 px-3 py-2 text-[12px] font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                      Confirm settled · {formatRupeesExact(a.ownerPayoutPaise)}
                    </button>
                    <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenForm('settle')}
                  className="rounded-lg bg-brand-500 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-brand-600"
                >
                  Mark settled ({formatRupeesExact(a.ownerPayoutPaise)})
                </button>
              )
            ) : null}

            {canRefund ? (
              openForm === 'refund' ? (
                <div className="rounded-lg border border-[var(--color-divider)] p-3">
                  <input
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    placeholder="Refund reference (optional)"
                    className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={isMutating}
                      onClick={() => onRefundDeposit(record.id, draft.trim() || undefined)}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Confirm deposit refund · {formatRupeesExact(a.depositPaise)}
                    </button>
                    <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => onOpenForm('refund')} className={secondaryBtn}>
                  Refund deposit ({formatRupeesExact(a.depositPaise)})
                </button>
              )
            ) : null}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default PaymentDetailDrawer;
